import * as React from 'react';

import { useAssistantMembershipPause } from '../../hooks/assistant/useAssistantMembershipPause';
import { useDrainPendingAlertExpand } from '../../hooks/assistant/useDrainPendingAlertExpand';
import { useWelcomeAlertCount } from '../../hooks/assistant/useWelcomeAlertCount';
import { useAssistantWebSocket } from '../../hooks/useAssistantWebSocket';
import { findHeldOpenAlertId } from '../../lib/assistant/alertCards';
import { useAssistantTurnStatus } from '../../providers/AssistantTurnStatusProvider';
import { useAuthState } from '../../providers/AuthProvider';
import { useNavigateWithDateRange } from '../../providers/DateRangeProvider';
import { useSidePanel } from '../../providers/SidePanelProvider';
import { entityDestination } from '../../utils/entityDestination';

import AssistantClientToolRunner from './AssistantClientToolRunner';
import AssistantPaneView, { type AssistantHost } from './AssistantPaneView';
import { useAlertCardWorkItems } from './useAlertCardWorkItems';
import { useComposerAlertsCenter } from './useComposerAlertsCenter';
import { useTranscriptEntities } from './useTranscriptEntities';

import type { EntityTarget } from '../../utils/entityDestination';
import type { IObservationDocument } from '@orn/global/assistant/snapshotMessage.js';

/** Module-level so the initial set is referentially stable across renders. */
const EMPTY_ALERT_IDS: ReadonlySet<string> = new Set();

/**
 * Top-level "Mantle Assistant" pane (route `/assistant`). Streams a
 * Claude-backed financial chat over WebSocket.
 *
 * Thin wrapper around {@link AssistantPaneView} — owns the hook, hands its
 * return shape to the presentational view. The split exists so Storybook
 * stories and visual-verification subagents can render the view from fixture
 * data without mocking the hook. The Notification Center card is wired here
 * (`useComposerAlertsCenter`) and passed down as slots for the same reason: it
 * fetches, and the view must not.
 *
 * Tool-call detail is not rendered. When the `debug-assistant` gate is on, the
 * server streams each tool's name / args / output on the live frames and the
 * hook logs them to the browser console as they complete.
 */
interface IAssistantPaneProps {
  /** Renders the header's close control — supplied by the `xl+` side column and the phone sheet. */
  onClose?: () => void;
  /** Where the pane is mounted; the sheet changes the close control and the full center's framing. */
  host?: AssistantHost;
}

const AssistantPane = ({ onClose, host }: IAssistantPaneProps) => {
  const { session } = useAuthState();

  // Chat auth is cookie-based — pass a boolean instead of any credential.
  // The browser attaches `_s` to the WS upgrade automatically.
  const {
    messages,
    isProcessing,
    isAbortable,
    isRehydrating,
    threadGeneration,
    connectionStatus,
    error,
    sendMessage,
    expandAlert,
    stopTurn,
    reclaim,
    pendingClientTools,
    sendClientToolResult,
  } = useAssistantWebSocket(session != null);

  // Publishes the turn state for the alert surfaces — a record page's banner
  // is outside this pane, so it cannot read `isProcessing` directly.
  // Layout-synchronous so the flag is readable before the paint of
  // the render that flipped `isProcessing`; a passive effect would leave a
  // painted frame where a click still passes the mid-turn gate. The cleanup
  // keeps the flag honest across unmount: no pane means no socket, and no
  // socket means no client-side turn.
  const { setTurnInFlight } = useAssistantTurnStatus();
  React.useLayoutEffect(() => {
    setTurnInFlight(isProcessing);
    return () => setTurnInFlight(false);
  }, [isProcessing, setTurnInFlight]);

  // Alerts opened while still unread. The recap treatment asks whether the
  // alert was new to the user, and the click that opens one also marks it read
  // — so by the time the reply renders, the stored status has already moved and
  // can no longer answer that. Every opener reports what the row said when it
  // was clicked, and this remembers it.
  const [freshlyOpenedAlertIds, setFreshlyOpenedAlertIds] = React.useState<ReadonlySet<string>>(EMPTY_ALERT_IDS);

  // The memory is per thread, not per tab. `/reset` replaces the transcript
  // without remounting this pane, so without this the exclusion would outlive
  // the conversation that justified it and a genuinely past alert would reopen
  // in the new thread reading as news.
  React.useEffect(() => {
    setFreshlyOpenedAlertIds(EMPTY_ALERT_IDS);
  }, [threadGeneration]);

  // The number the welcome header states — frozen per welcome row by the
  // hook, from the same cache entry the bell and the Notification Center read.
  // `null` renders the greeting alone.
  const welcomeAlertCount = useWelcomeAlertCount(messages);

  // The name the welcome greets by: non-blank preferred name over first name
  // (the onboarding headline's rule). Neither present → null, and the view
  // renders only the row's dated divider, skipping the greeting itself.
  const viewerGreetingName =
    (session?.user.preferredName ?? '').trim() || (session?.user.firstName ?? '').trim() || null;

  const { noteAlertExpanded, noteUserSent } = useSidePanel();
  const membershipPause = useAssistantMembershipPause();

  // Every path that puts an alert into the thread ends here, so this is also
  // where the Notification Center card learns to get out of the way.
  const openAlert = React.useCallback(
    (alertId: string, wasUnread: boolean) => {
      if (wasUnread) {
        setFreshlyOpenedAlertIds((previous) => new Set(previous).add(alertId));
      }
      expandAlert(alertId);
      noteAlertExpanded();
    },
    [expandAlert, noteAlertExpanded]
  );

  // A record page's banner cannot reach `expandAlert` — it is outside this
  // pane, and on the route the pane is a different page — so it leaves the
  // alert on the panel context and this drains it once the pane is up.
  // While paused the expand is dropped rather than drained: it would redial a
  // socket the server closes with `4004`, and the pane is showing why already.
  const drainAlert = React.useCallback(
    (alertId: string, wasUnread: boolean) => {
      if (membershipPause === null) {
        openAlert(alertId, wasUnread);
      }
    },
    [membershipPause, openAlert]
  );
  useDrainPendingAlertExpand(drainAlert);

  // Read off the transcript rather than set in `openAlert`: the expand is
  // fire-and-forget, and only a card that actually landed is being shown.
  const selectedAlertId = React.useMemo(() => findHeldOpenAlertId(messages), [messages]);

  const composerAlerts = useComposerAlertsCenter({ onExpandAlert: openAlert, selectedId: selectedAlertId });

  // The card collapses on the session's first send, whether typed or tapped.
  const handleSend = React.useCallback(
    (content: string, attachments?: IObservationDocument[]) => {
      noteUserSent();
      sendMessage(content, attachments);
    },
    [noteUserSent, sendMessage]
  );

  // Hoisted for the same reason the Notification Center reads are: the view fetches nothing.
  const { cards: alertCards, workedAlertIds } = useAlertCardWorkItems(messages);
  const entities = useTranscriptEntities(messages);

  // Run client tools one at a time — the head of the queue — so two browser
  // flows (e.g. two Plaid modals) never overlap. When the head reports its
  // result it leaves the queue and the next invocation takes over.
  const activeClientTool = pendingClientTools[0];

  // A chip click is a send whose subject is known: the view passes the alert
  // its reply expanded, and the id rides the wire part so the server can
  // ground the turn in that row (see `UserMessagePart.sourceAlertId`).
  const handleSuggestionSelect = React.useCallback(
    (text: string, sourceAlertId?: string) => {
      noteUserSent();
      sendMessage(text, undefined, sourceAlertId);
    },
    [noteUserSent, sendMessage]
  );

  const navigate = useNavigateWithDateRange();

  // Opening a chip's row belongs to the pane, not the view, the way the lookups
  // do: the view renders chips, the pane owns where they go.
  //
  // At `xl+` a navigate swaps the content column beside this pane and the
  // conversation stays open; on the route the assistant *is* the page, so the
  // same click leaves it, and on the phone sheet the new entry closes the
  // sheet over the page it opens. That is deliberate and matches an alert
  // row's click.
  const handleEntitySelect = React.useCallback(
    (target: EntityTarget) => {
      void navigate(entityDestination(target).path);
    },
    [navigate]
  );

  return (
    <>
      <AssistantPaneView
        messages={messages}
        isProcessing={isProcessing}
        isAbortable={isAbortable}
        isRehydrating={isRehydrating}
        // A pending client tool means the turn is parked on the browser (the user
        // is finishing the Plaid flow) — suppress the "Thinking…" spinner.
        isAwaitingClientTool={activeClientTool !== undefined}
        connectionStatus={connectionStatus}
        error={error}
        onSend={handleSend}
        onStop={stopTurn}
        onReclaim={reclaim}
        onClose={onClose}
        host={host}
        alertCards={alertCards}
        workedAlertIds={workedAlertIds}
        viewerUserId={session?.user.id ?? null}
        viewerGreetingName={viewerGreetingName}
        welcomeAlertCount={welcomeAlertCount}
        freshlyOpenedAlertIds={freshlyOpenedAlertIds}
        entities={entities}
        onSuggestionSelect={handleSuggestionSelect}
        onEntitySelect={handleEntitySelect}
        alertsSlot={composerAlerts.alertsSlot}
        alertsPresentation={composerAlerts.alertsPresentation}
        alertsMotion={composerAlerts.alertsMotion}
        alertsReservedHeightPx={composerAlerts.alertsReservedHeightPx}
        composerNoticeSlot={composerAlerts.composerNoticeSlot}
        isCoveredByAlerts={composerAlerts.isCoveredByAlerts}
        membershipPause={membershipPause}
      />
      {/* Runs a browser-side client tool (e.g. Plaid Link) when the model calls
          one, then reports the result. Keyed on the invocation so the next
          queued tool remounts and a server cancel unmounts + tears down. */}
      {activeClientTool && (
        <AssistantClientToolRunner
          key={activeClientTool.toolId}
          invocation={activeClientTool}
          onResult={sendClientToolResult}
        />
      )}
    </>
  );
};

export default AssistantPane;
